import {
	type ComponentType,
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react'
import {
	addMonths,
	addWeeks,
	endOfMonth,
	endOfWeek,
	startOfMonth,
	startOfWeek,
	subMonths,
	subWeeks,
} from 'date-fns'

import { DefaultCalendarEvent } from './default-calendar-event'
import { EventPreviewDefault } from './event-preview-default'
import type {
	RenderSelectedEvent,
	SchedulerCalendarEvent,
	SchedulerCalendarEventComponentProps,
	SchedulerCalendarEventPreviewProps,
	SchedulerCalendarLocale,
	SchedulerCalendarView,
	SchedulerVisiblePeriod,
} from './types'

type SchedulerCalendarContextValue = {
	selectedDate: Date | null
	currentDate: Date
	events: SchedulerCalendarEvent[]
	selectedEventId: string | null
	selectedEvent: SchedulerCalendarEvent | null
	visiblePeriod: SchedulerVisiblePeriod | null
	view: SchedulerCalendarView
	locale: SchedulerCalendarLocale
	weekStartHour: number
	weekEndHour: number
	EventComponent: ComponentType<SchedulerCalendarEventComponentProps>
	EventPreview: ComponentType<SchedulerCalendarEventPreviewProps>
	onSelectEvent?: (event: SchedulerCalendarEvent | null) => void
	goNext: () => void
	goPrev: () => void
	setCurrentDate: (date: Date) => void
	setSelectedEventId: (eventId: string | null) => void
	setView: (view: SchedulerCalendarView) => void
	setSelectedDate: (date: Date | null) => void
	renderSelectedEvent?: ({
		event,
	}: {
		event: SchedulerCalendarEvent<unknown>
	}) => ReactNode | null
}

const SchedulerCalendarContext = createContext<
	SchedulerCalendarContextValue | undefined
>(undefined)

export const SchedulerCalendarProvider = ({
	children,
	defaultView = 'week',
	renderSelectedEvent = () => null,
	events,
	EventComponent = DefaultCalendarEvent,
	EventPreview = EventPreviewDefault,
	locale = 'fr',
	weekStartHour = 6,
	weekEndHour = 23,
	onSelectEvent,
}: {
	children: ReactNode
	defaultView?: SchedulerCalendarView
	renderSelectedEvent?: RenderSelectedEvent<unknown>
	events: SchedulerCalendarEvent[]
	EventComponent?: ComponentType<SchedulerCalendarEventComponentProps>
	EventPreview?: ComponentType<SchedulerCalendarEventPreviewProps>
	locale?: SchedulerCalendarLocale
	weekStartHour?: number
	weekEndHour?: number
	onSelectEvent?: (event: SchedulerCalendarEvent | null) => void
}) => {
	const [selectedDate, setSelectedDate] = useState<Date | null>(null)
	const [currentDate, setCurrentDate] = useState(() => new Date())
	const [selectedEventId, setSelectedEventId] = useState<string | null>(null)
	const [view, setView] = useState<SchedulerCalendarView>(defaultView)
	const visiblePeriod = useMemo((): SchedulerVisiblePeriod | null => {
		if (view === 'week') {
			const weekStartsOn = locale === 'en' ? 0 : 1
			const weekStart = startOfWeek(currentDate, { weekStartsOn })
			const weekEnd = endOfWeek(currentDate, { weekStartsOn })

			return { end: weekEnd, start: weekStart }
		}

		if (view === 'month') {
			return {
				end: endOfMonth(currentDate),
				start: startOfMonth(currentDate),
			}
		}

		return null
	}, [currentDate, locale, view])

	const value = useMemo(
		() => ({
			currentDate,
			events,
			goNext: () => {
				setCurrentDate((date) => {
					if (view === 'week') return addWeeks(date, 1)
					if (view === 'month') return addMonths(date, 1)

					return date
				})
			},
			goPrev: () => {
				setCurrentDate((date) => {
					if (view === 'week') return subWeeks(date, 1)
					if (view === 'month') return subMonths(date, 1)

					return date
				})
			},
			setCurrentDate,
			selectedDate,
			selectedEventId,
			selectedEvent: events.find(({ id }) => id === selectedEventId) ?? null,
			setSelectedEventId,
			setSelectedDate,
			visiblePeriod,
			renderSelectedEvent,
			view,
			setView,
			locale,
			weekStartHour,
			weekEndHour,
			EventComponent,
			EventPreview,
			onSelectEvent,
		}),
		[
			currentDate,
			events,
			selectedDate,
			selectedEventId,
			renderSelectedEvent,
			view,
			visiblePeriod,
			locale,
			weekStartHour,
			weekEndHour,
			EventComponent,
			EventPreview,
			onSelectEvent,
		],
	)

	useEffect(() => {
		if (!selectedEventId) return
		onSelectEvent?.(events.find(({ id }) => id === selectedEventId) ?? null)
	}, [events, onSelectEvent, selectedEventId])

	useEffect(() => {
		const handlePointerDown = (event: PointerEvent) => {
			if (!selectedEventId) return

			const path = event.composedPath() as (EventTarget | null)[]
			const clickedInsideInteractive = path.some((target) => {
				if (!(target instanceof HTMLElement)) return false
				return (
					target.dataset.schedulerEvent === 'true' ||
					target.dataset.schedulerActiveEventCard === 'true'
				)
			})

			if (clickedInsideInteractive) return

			setSelectedEventId(null)
			onSelectEvent?.(null)
		}

		document.addEventListener('pointerdown', handlePointerDown)
		return () => {
			document.removeEventListener('pointerdown', handlePointerDown)
		}
	}, [onSelectEvent, selectedEventId])

	return (
		<SchedulerCalendarContext.Provider value={value}>
			{children}
		</SchedulerCalendarContext.Provider>
	)
}

export const useSchedulerCalendar = () => {
	const context = useContext(SchedulerCalendarContext)

	if (!context) {
		throw new Error(
			'useSchedulerCalendar must be used within a SchedulerCalendarProvider',
		)
	}

	return context
}
